
import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Mail, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Metrics {
  scheduled: number;
  available: number;
  total: number;
}

interface DashboardMetricsProps {
  metrics: Metrics | undefined;
  isLoading: boolean;
}

export function DashboardMetrics({ metrics, isLoading }: DashboardMetricsProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-slate-200 h-10 w-10"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cardData = [
    {
      title: "Cartas Agendadas",
      value: metrics?.scheduled || 0,
      icon: Clock,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      linkTo: "/admin/cartas-agendadas"
    },
    {
      title: "Cartas Disponíveis",
      value: metrics?.available || 0,
      icon: Mail,
      color: "text-green-600",
      bgColor: "bg-green-50",
      linkTo: "/admin/cartas-disponiveis"
    },
    {
      title: "Total de Cartas",
      value: metrics?.total || 0,
      icon: Calendar,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
  ];

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {cardData.map((card) => (
        <Card key={card.title} className="overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300">
          <div className={`h-2 ${card.bgColor} w-full`}></div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-xl ${card.bgColor}`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-600">{card.title}</h3>
                  <p className="text-3xl font-bold mt-1">{card.value}</p>
                </div>
              </div>
              {card.linkTo && (
                <Button 
                  variant="ghost" 
                  onClick={() => navigate(card.linkTo)}
                  className="hover:bg-gray-100"
                >
                  Ver
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
